<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @academicYear="onAcademicYearChange"
      @provider="onProviderChange"
      @search="onSearchHandler"
      @serviceType="onServiceTypeChange"
      @status="onStatusChange"
    />
    <custom-separator class="my-4" text="Resultado de Especialistas" />
    <custom-result-not-found
      v-if="results.length == 0"
      :text="'No cuenta con información disponible.'"
    />
    <template v-else>
      <specialist-result-row
        v-for="(row, idx) of results"
        :key="`result-${idx}`"
        class="mb-3"
        :data="row"
        @on-view-specialist="onViewSpecialistHandler(row)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import SpecialistResultRow from './components/SpecialistResultRow';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import {
  getInitFilters as _getInitFilters,
  getSpecialistsByProvider as _getSpecialistsByProvider,
  getDisciplinesByServiceType as _getDisciplinesByServiceType,
  search as _search,
} from '@/services/provider/specialist.api.js';
import { getProvidersByAcademicYear as _getProvidersByAcademicYear } from '@/services/provider/provider.api.js';
import {
  getMunicipalities as _getMunicipalities,
  getMunicipalitiesByProvider as _getMunicipalitiesByProvider,
} from '@/services/common/location.api.js';
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
import roles from '@/utils/constants/roles.js';

const DEFAULT_PAGE_SIZE = 20;
const SPECIALIST_MANAGEMENT_ROUTE_NAME = 'specialistManagement';

export default {
  name: 'SpecialistManagementMain',
  mixins: [alertMixin, storeMixin],
  components: {
    PageHeader,
    Pagination,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    SpecialistResultRow,
  },
  data: () => ({
    moduleName: storeModuleNames.ProviderSpecialistModuleName,
    filterModel: {
      academicYear: null,
      provider: null,
      specialist: null,
      municipality: null,
      serviceType: null,
      disciplineType: null,
      locationType: null,
      status: null,
    },
    filterOptions: {
      academicYears: [],
      providers: [],
      specialists: [],
      municipalities: [],
      serviceTypes: [],
      disciplineTypes: [],
      locationTypes: [],
      statuses: [],
    },
    municipalities: [],
    specialists: [],
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Especialistas',
        icon: 'fas fa-user-tie',
        routes: ['Inicio', 'Proveedor', 'Especialistas'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus pr-2',
          variant: 'success ml-0',
          visible: this.hasAddPermission,
          click: () => this.onAddSpecialistHandler(),
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: false,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-3',
          validations: 'required',
          error: 'El nombre del proveedor es requerido.',
          disabled: this.disableProviderDropdown,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterOptions.specialists,
          placeholder: 'Especialista',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.filterOptions.municipalities,
          placeholder: 'Seleccione',
          label: 'Municipio:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.filterOptions.serviceTypes,
          placeholder: 'Seleccione',
          label: 'Tipo de Servicio:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplineTypes,
          placeholder: 'Seleccione',
          label: 'Disciplina:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
        {
          name: 'locationType',
          component: 'CustomDropDown',
          options: this.filterOptions.locationTypes,
          placeholder: 'Seleccione',
          label: 'Tipo de Lugar:',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          isAdvanced: true,
          class: 'col-12 col-md-3',
        },
      ];
    },
    hasAddPermission() {
      return this.userHasPermissions(
        'menu.rempro.specialists.view.addspecialist'
      );
    },
    hasProviderRole() {
      return this.userRoleId === roles.Provider_RP;
    },
    disableProviderDropdown() {
      return this.userHasSingleCompany && this.filterModel.provider != null;
    },
    userHasSingleCompany() {
      return this.userCompanies?.length === 1;
    },
    userCurrentCompany() {
      return this.userHasSingleCompany ? this.userCompanies[0] : null;
    },
    hasPersistInfo() {
      return Boolean(this.moduleData?.SpecialistManagementMain || false);
    },
    hasSpecialistRpRole() {
      return this.fullProfile?.roleid?.toUpperCase() === roles.Specialist_RP;
    },
    isActiveFilter() {
      return this.filterModel.status?.id == 0
        ? true
        : this.filterModel.status?.id == 1
        ? false
        : null;
    },
  },
  async mounted() {
    await this.getInitFilters();
    if (this.moduleData?.SpecialistManagementMain) this.searchSpecialists();
    if (this.filterModel?.provider?.id)
      this.onProviderChange(this.filterModel.provider);
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async getInitFilters() {
      const { filters, collections } = (await _getInitFilters()).data;
      if (Object.values(filters).length && Object.values(collections).length) {
        this.filterModel = {
          ...this.filterModel,
          ...filters,
          academicYear: this.hasPersistInfo
            ? this.filterModel.academicYear
            : filters.academicYear,
        };
        this.filterOptions = {
          ...this.filterOptions,
          ...collections,
        };

        if (this.hasProviderRole) {
          await this.onProviderChange(this.userCurrentCompany);
          return;
        }

        this.municipalities = collections.municipalities;
      }
    },
    async getMunicipalities(academicYearId) {
      this.filterModel.municipality = null;
      this.filterOptions.municipalities = (
        await _getMunicipalities(academicYearId)
      ).data;
    },
    async onAddSpecialistHandler() {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      const academicYearId = this.filterModel.academicYear?.id;
      const companyId = this.filterModel.provider?.id;

      this.$router.push({
        name: SPECIALIST_MANAGEMENT_ROUTE_NAME,
        params: {
          companyId,
          academicYearId,
        },
      });
    },
    onViewSpecialistHandler({ companyId, specialistId, academicYearId }) {
      if (specialistId) {
        this.$router.push({
          name: SPECIALIST_MANAGEMENT_ROUTE_NAME,
          params: {
            companyId,
            specialistId,
            academicYearId,
          },
        });
      }
    },
    async onAcademicYearChange(value) {
      if (value?.id) {
        await this.getMunicipalities(value.id);
      }
    },
    async onProviderChange(value) {
      this.filterModel.municipality = null;
      this.filterModel.specialist = null;

      if (value?.id && this.filterModel.academicYear?.id) {
        const academicYearId = this.filterModel.academicYear.id;
        this.filterOptions.municipalities = (
          await _getMunicipalitiesByProvider(academicYearId, value.id)
        ).data;
        this.filterOptions.specialists = (
          await _getSpecialistsByProvider(
            academicYearId,
            value.id,
            this.isActiveFilter
          )
        ).data;

        return;
      }

      this.filterOptions.municipalities = this.municipalities;
      this.filterOptions.specialists = this.specialists;
    },
    async onStatusChange() {
      this.filterModel.specialist = null;
      this.filterOptions.specialists = [];
      if (this.filterModel.academicYear?.id && this.filterModel.provider?.id) {
        const academicYearId = this.filterModel.academicYear.id;
        const providerId = this.filterModel.provider.id;
        this.filterOptions.specialists = (
          await _getSpecialistsByProvider(
            academicYearId,
            providerId,
            this.isActiveFilter
          )
        ).data;
      }
    },
    async onServiceTypeChange(value) {
      this.filterModel.disciplineType = null;
      this.filterOptions.disciplineTypes = [];
      if (value?.id) {
        this.filterOptions.disciplineTypes = (
          await _getDisciplinesByServiceType(value.id)
        ).data;
      }
    },
    async onSearchHandler() {
      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      this.pagination.currentPage = 1;
      this.searchSpecialists();
    },
    async searchSpecialists() {
      const payload = this.fillPayload();

      const res = (await _search(payload)).data;
      this.results = res.data;
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      this.searchSpecialists();
    },
    fillPayload() {
      const payload = {
        academicYearId: this.filterModel.academicYear?.id,
        companyId: this.filterModel.provider?.id,
        specialistId: this.filterModel.specialist?.id,
        municipalityId: this.filterModel.municipality?.id,
        serviceTypeId: this.filterModel.serviceType?.id,
        disciplineTypeId: this.filterModel.disciplineType?.id,
        locationTypeId: this.filterModel.locationType?.id,
        isActive:
          this.filterModel.status?.id == 0
            ? true
            : this.filterModel.status?.id == 1
            ? false
            : null,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      return payload;
    },
  },
  watch: {
    async 'filterModel.academicYear'(newValue) {
      if (this.hasSpecialistRpRole) {
        return;
      }

      const { data } = await _getProvidersByAcademicYear(newValue.id);
      this.filterOptions.providers = data;
      this.filterModel.provider = null;

      if (this.hasProviderRole) {
        const currentCompanyIsSelectable = data.find(
          (provider) => provider.id == this.userCurrentCompany.id
        );
        this.filterOptions.providers = currentCompanyIsSelectable
          ? [this.userCurrentCompany]
          : [];
        this.filterModel.provider = currentCompanyIsSelectable
          ? this.userCurrentCompany
          : null;
      }

      this.filterModel.specialist = null;
      this.onProviderChange(this.filterModel.provider);
    },
  },
};
</script>
